// Walidacja kliencka: on-blur per pole, bramka per krok z podsumowaniem
// błędów. Komunikaty PL, konkretne, bez żargonu. Serwer waliduje ponownie.

import { fieldId, focusHeading } from "./a11y.js";
import { HONEYPOT } from "./config.js";

const MESSAGES = {
    required: "To pole jest wymagane.",
    radio: "Wybierz jedną z opcji.",
    email: "Sprawdź adres e-mail — wygląda na niepełny.",
    tel: "Sprawdź numer telefonu — wygląda na za krótki.",
    url: "Ten link wygląda na niepełny — sprawdź go.",
    rodo: "Bez zgody na kontakt nie możemy przygotować oferty.",
};

// Slot błędu: najpierw po id pola (id + "-err"), potem najbliższy slot
// w opakowaniu pola, na końcu id wyliczone z name. Kolejność ma znaczenie:
// tam gdzie input_name != klucz schemy (uploady: zdjecia[], logo, teksty_plik;
// pole „teksty" na stronie materiałów) wyliczenie z name trafia w pustkę.
function errorSlotFor(input) {
    if (input.id) {
        const byId = document.getElementById(input.id + "-err");
        if (byId) return byId;
    }
    const wrap = input.closest(".brief__field, .brief__group, .brief__upload, .brief__consent");
    if (wrap) {
        const slot = wrap.querySelector(".brief__fieldError");
        if (slot) return slot;
    }
    return document.getElementById(fieldId(input.name || "") + "-err");
}

export function setFieldError(input, message) {
    const slot = errorSlotFor(input);
    if (slot) {
        slot.textContent = message;
        slot.hidden = false;
    }
    input.setAttribute("aria-invalid", "true");
}

export function clearFieldError(input) {
    const slot = errorSlotFor(input);
    if (slot) {
        slot.textContent = "";
        slot.hidden = true;
    }
    input.removeAttribute("aria-invalid");
}

// Grupa radio: błąd czyścimy/ustawiamy raz per name.
function radioGroupChecked(form, name) {
    return !!form.querySelector('input[name="' + CSS.escape(name) + '"]:checked:not(:disabled)');
}

// Walidacja jednego pola; zwraca komunikat albo null.
export function validateField(input, form) {
    if (input.disabled || input.closest("[hidden]")) return null;
    return validateValue(input, form);
}

// Sama reguła, bez oglądania się na widoczność. Potrzebna tam, gdzie pytamy
// „czy ten krok jest kompletny", a formularz akurat jest ukryty (lista sekcji,
// deep-linki) — inaczej wszystko wychodzi poprawne, bo nic nie jest widoczne.
function validateValue(input, form) {
    const type = input.dataset.validate || "";
    const value = (input.value || "").trim();

    if (input.type === "radio") {
        const required =
            input.hasAttribute("required") || input.hasAttribute("data-required-when-visible");
        if (required && !radioGroupChecked(form, input.name)) return MESSAGES.radio;
        return null;
    }
    if (input.type === "checkbox") {
        if (input.hasAttribute("required") && !input.checked) {
            return input.name === "zgl_32_rodo" ? MESSAGES.rodo : MESSAGES.required;
        }
        return null;
    }

    if (input.hasAttribute("required") && value === "") {
        // required_unless: to samo pytanie ma drugą, równoprawną drogę
        // (kolory słowami ALBO wskazane próbnikiem) — nie pytamy dwa razy
        const alt = input.dataset.requiredUnless;
        if (alt) {
            const other = form.querySelector('[name="' + CSS.escape(alt) + '"]');
            if (other && (other.value || "").trim() !== "") return null;
        }
        return input.dataset.requiredMessage || MESSAGES.required;
    }
    if (value === "") return null;

    if (type === "email") {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) return MESSAGES.email;
    }
    if (type === "tel") {
        if (value.replace(/\D/g, "").length < 7) return MESSAGES.tel;
    }
    if (type === "url") {
        const candidate = /^https?:\/\//i.test(value) ? value : "https://" + value;
        try {
            const parsed = new URL(candidate);
            if (!parsed.hostname.includes(".")) return MESSAGES.url;
        } catch {
            return MESSAGES.url;
        }
    }
    return null;
}

// Walidacja widocznych pól kroku; wypełnia inline errors + podsumowanie.
// Zwraca true gdy czysto. opts.focus=false: bez przenoszenia focusa
// (masowa walidacja przed wysyłką).
export function validateStep(stepEl, form, opts = {}) {
    const summary = stepEl.querySelector(".brief__errorSummary");
    const errors = [];
    const seenRadioGroups = new Set();

    stepEl.querySelectorAll("input, textarea, select").forEach((input) => {
        if (input.disabled || input.type === "file") return;
        if (input.type === "radio") {
            if (seenRadioGroups.has(input.name)) return;
            seenRadioGroups.add(input.name);
        }
        // od tej chwili pole jest „uzbrojone": pusty required zapala błąd
        // także przy zwykłym blurze (patrz initValidate)
        input.dataset.armed = "1";
        const message = validateField(input, form);
        if (message) {
            setFieldError(input, message);
            errors.push({ input, message });
        } else {
            clearFieldError(input);
        }
    });

    if (!summary) return errors.length === 0;

    if (errors.length === 0) {
        summary.hidden = true;
        summary.innerHTML = "";
        return true;
    }

    const intro = document.createElement("p");
    intro.textContent =
        errors.length === 1 ? "Popraw 1 pole, żeby przejść dalej:" : "Popraw pola, żeby przejść dalej:";
    const list = document.createElement("ul");
    errors.forEach(({ input, message }) => {
        const li = document.createElement("li");
        const link = document.createElement("a");
        const label = labelTextFor(input);
        link.href = "#" + (input.id || fieldId(input.name));
        link.textContent = (label ? label + " — " : "") + message;
        link.addEventListener("click", (event) => {
            event.preventDefault();
            input.focus();
            input.scrollIntoView({ block: "center" });
        });
        li.appendChild(link);
        list.appendChild(li);
    });
    summary.innerHTML = "";
    summary.appendChild(intro);
    summary.appendChild(list);
    summary.hidden = false;
    if (opts.focus !== false) focusHeading(summary);

    return false;
}

// Cicha kontrola kompletności kroku — bez dotykania UI (deep-linki, statusy
// listy sekcji). validateStep zostawia po sobie komunikaty, więc do sprawdzeń
// „czy można tu wejść" potrzebna jest osobna, bezstanowa ścieżka.
export function stepIsComplete(stepEl, form) {
    const seen = new Set();
    let ok = true;
    stepEl.querySelectorAll("input, textarea, select").forEach((input) => {
        if (!ok || input.disabled || input.type === "file") return;
        // pole w zwiniętej gałęzi wewnątrz kroku pomijamy; ukrycie samego
        // kroku (bo widać listę sekcji) nie może udawać, że krok jest gotowy
        const collapsed = input.closest("[hidden]");
        if (collapsed && collapsed !== stepEl && stepEl.contains(collapsed)) return;
        if (input.type === "radio") {
            if (seen.has(input.name)) return;
            seen.add(input.name);
        }
        if (validateValue(input, form)) ok = false;
    });
    return ok;
}

// Czy w kroku jest cokolwiek wypełnionego (status „W trakcie" na liście sekcji).
export function stepHasContent(stepEl) {
    let any = false;
    stepEl.querySelectorAll("input, textarea, select").forEach((input) => {
        if (any || input.disabled || input.type === "file") return;
        if (input.type === "radio" || input.type === "checkbox") {
            if (input.checked && !input.defaultChecked) any = true;
            return;
        }
        if ((input.value || "").trim() !== "") any = true;
    });
    return any;
}

function labelTextFor(input) {
    const group = input.closest("fieldset");
    if (input.type === "radio" && group) {
        const legend = group.querySelector("legend");
        if (legend) return legend.textContent.replace("*", "").trim();
    }
    if (input.id) {
        const label = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        if (label) return label.textContent.replace("*", "").replace("(opcjonalne)", "").trim();
    }
    return "";
}

// „Reward early, punish late": pole nietknięte i puste nie dostaje błędu przy
// samym przejściu tabem — dopiero po pierwszej próbie przejścia dalej
// (validateStep uzbraja pola). Pole z wartością walidujemy na blur od razu
// (format), a pole już błędne naprawiamy na input, znak po znaku.
export function initValidate(form) {
    form.addEventListener(
        "blur",
        (event) => {
            const input = event.target;
            if (!input.matches("input:not([type=file]), textarea, select")) return;
            if (input.name === HONEYPOT) return;

            const isChoice = input.type === "radio" || input.type === "checkbox";
            const empty = !isChoice && (input.value || "").trim() === "";
            if (empty && input.dataset.armed !== "1") {
                clearFieldError(input);
                return;
            }

            const message = validateField(input, form);
            if (message) setFieldError(input, message);
            else clearFieldError(input);
        },
        true
    );

    form.addEventListener("input", (event) => {
        const input = event.target;
        if (!input.getAttribute) return;
        if (input.getAttribute("aria-invalid") === "true") {
            if (!validateField(input, form)) clearFieldError(input);
        }
        // pole, którego wymagalność zdejmuje to pole (required_unless),
        // musi zgasnąć od razu — inaczej klient wskazuje kolor próbnikiem,
        // a błąd przy polu tekstowym dalej krzyczy
        if (input.name) {
            form.querySelectorAll('[data-required-unless="' + CSS.escape(input.name) + '"]').forEach((dep) => {
                if (dep.getAttribute("aria-invalid") === "true" && !validateField(dep, form)) {
                    clearFieldError(dep);
                }
            });
        }
    });

    // URL-e: auto-dopisanie https:// przy wyjściu z pola.
    form.addEventListener(
        "blur",
        (event) => {
            const input = event.target;
            if (input.dataset && input.dataset.validate === "url") {
                const value = (input.value || "").trim();
                if (value !== "" && !/^https?:\/\//i.test(value) && value.includes(".")) {
                    input.value = "https://" + value;
                }
            }
        },
        true
    );
}
